import React, { useMemo, useEffect, useState, useCallback } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { fetchLinkedAccounts, fetchCoWorkers } from "../http/account.js";
import {Alert} from "../components/Ui/Alert.jsx";
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { extractHHMM, formatDateToDDMMYYYY } from "../components/Helpers/dateFns.js";
import { fetchClinicAppointments } from "../http/clinics.js";
import Clinic from "../components/Ui/Clinic/Clinic.jsx";
import { DefaultButton } from "../components/StyledComponents/Buttons/Buttons.jsx";

const TableButton = styled.button`
  padding: 5px 5px;
  background-color: #007bff;
  color: white;
  font-size: 12px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin: 0 1px;

  &:hover {
    background-color: #0056b3;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  padding-top: 10px;
  margin: 0 0 20px 0;
  font-size:15px;
`;

export default function Clinics({ colour, email }) {

  const [showClinic, setShowClinic] = useState(false);
  const [isNewClinic, setIsNewClinic] = useState(false);
  const [currentClinic, setCurrentClinic] = useState(null);

  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [clinics, setClinics] = useState([]);
  const [clinicCount, setClinicCount] = useState(0);
  const [coWorkers, setCoWorkers] = useState([]);

  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  useEffect(() => {
    const loadLinkedAccounts = async () => {
      setIsLoading(true);
      try {
        const accounts = await fetchLinkedAccounts(email);
        const userAccounts = [];

        accounts.forEach(account => {
          userAccounts.push({ label: account.accountdescrip, value: account.accountid });
        });

        setLinkedAccounts(userAccounts);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadCoWorkers = async () => {
      setIsLoading(true);
      try {
        const coWorkers = await fetchCoWorkers();
        const coWorkerList = [];

        coWorkers.forEach(coWorker => {
          coWorkerList.push({ label: coWorker.fullname, value: coWorker.eaddress, account: coWorker.accountid });
        });

        setCoWorkers(coWorkerList);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadLinkedAccounts();
    loadCoWorkers();
  }, [email]);

  function ShowHideClinic(refresh = false) {
    setShowClinic(!showClinic);

    if (refresh) {
      loadClinics(pagingTracker.account, pagingTracker.currentPage, pagingTracker.pageSize);
    }
  }

  const loadClinics = useCallback(async (account, page, pageSize, filterStatus = null, searchValue = null, coWorker = null) => {
    setIsLoading(true);

    if (account || linkedAccounts) {
      try {
        const fetchedClinics = await fetchClinicAppointments((account ? account : linkedAccounts[0].value), page, pageSize, coWorker);

        if (!fetchedClinics[0].Appointments.length) {
          setClinics([]);
          setClinicCount(0);
        }

        if (fetchedClinics && fetchedClinics[0].Appointments) {
          const restructuredClinics = fetchedClinics[0].Appointments.map(clinic => ({
            ...clinic,
            dateFrm: formatDateToDDMMYYYY(clinic.clinicdate),
            startTimeFrm: extractHHMM(clinic.starttime),
            endTimeFrm: extractHHMM(clinic.endtime),
          }));

          setClinics(restructuredClinics);
          setClinicCount(fetchedClinics[0].Count);

        }

        setError(null);
      } catch (e) {
        setError(e);
        setClinics([]);
        setClinicCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [email, linkedAccounts]);

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "dateFrm",
      },
      {
        Header: "Optician/Dispenser",
        accessor: "companyname",
      },
      {
        Header: "Start Time",
        accessor: "startTimeFrm",
      },
      {
        Header: "End Time",
        accessor: "endTimeFrm",
      },
      {
        Header: "",
        accessor: "actions",
        Cell: ({ row }) => (
          <TableButton onClick={() => {
            setCurrentClinic(row.original);
            setShowClinic(true);
            setIsNewClinic(false);
          }}>
            Manage Clinic
          </TableButton>
        )
      },
    ]
  );



  return (
    <>
      {showClinic &&
        <Clinic
          isNewClinic={isNewClinic}
          showHideModal={ShowHideClinic}
          isModalOpen={showClinic}
          colours={colour}
          clinic={currentClinic} />}
      <CenterContainer>
        <FormContainer>
          <Card
            label={'Clinics'}
            headercolour={colour.sidebar}
            headerTextColour={colour.font}>
            <Heading>Clinic Management</Heading>
            <Paragraph>Select a clinic from the below list, or create a new clinic.</Paragraph>

            <ServerSidePagingTable
              columns={columns}
              pagingFn={loadClinics}
              data={clinics}
              recordCount={clinicCount}
              setPagingTracker={setPagingTracker}
              arrAccountFilters={linkedAccounts}
              arrCoWorkersFilter={coWorkers}>
              <DefaultButton onClick={() => {
                setIsNewClinic(true);
                setShowClinic(true);
              }}>Create Clinic</DefaultButton>
            </ServerSidePagingTable>
            {(error && !linkedAccounts) && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
            {!clinicCount && <Alert type="information">No clinics found.</Alert>}
          </Card>
        </FormContainer>
      </CenterContainer>
    </>
  );
}